/**
 * Pure formatting helpers for the "Sessions & devices" list. Kept separate
 * from the hook/component so the UA parsing + relative-time logic is unit
 * testable without React.
 */

import i18n from '@/i18n';

/**
 * Coarse "Browser · OS" label from a stored User-Agent hint. Order matters:
 * Edge/Opera UAs also contain "Chrome", and Chrome UAs also contain
 * "Safari", so the more specific brands are matched first.
 */
export function deviceLabel(uaHint: string | null | undefined): string {
  if (!uaHint) return i18n.t('lib2.device.unknown');
  const ua = uaHint;
  const browser = /edg/i.test(ua)
    ? 'Edge'
    : /opr|opera/i.test(ua)
      ? 'Opera'
      : /chrome|crios/i.test(ua)
        ? 'Chrome'
        : /firefox|fxios/i.test(ua)
          ? 'Firefox'
          : /safari/i.test(ua)
            ? 'Safari'
            : null;
  const os = /windows/i.test(ua)
    ? 'Windows'
    : /android/i.test(ua)
      ? 'Android'
      : /iphone|ipad|ios/i.test(ua)
        ? 'iOS'
        : /mac os|macintosh/i.test(ua)
          ? 'macOS'
          : /linux/i.test(ua)
            ? 'Linux'
            : null;
  const parts = [browser, os].filter(Boolean) as string[];
  return parts.length ? parts.join(' · ') : i18n.t('lib2.device.browser');
}

/** Relative "last seen" label in Russian. `now` is injectable for tests. */
export function lastSeenLabel(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return i18n.t('lib2.lastSeen.justNow');
  const min = Math.round(sec / 60);
  if (min < 60) return i18n.t('lib2.lastSeen.minutes', { count: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return i18n.t('lib2.lastSeen.hours', { count: hr });
  const days = Math.round(hr / 24);
  if (days < 30) return i18n.t('lib2.lastSeen.days', { count: days });
  return new Date(iso).toLocaleDateString('ru-RU');
}
